/**
 * TRANSITIONAL: translation between the inspector's property-path dialect and the
 * project document's (see plans/one-address-vocabulary.md). There is meant to be
 * ONE grammar — the document's — so this module shrinks as call sites migrate and
 * ends up holding only the AI-boundary tolerance shim.
 *
 * - inspector: `materials[0].color` — dots, bracket indices, no escaping, so a
 *   key containing `.` or `[` cannot be expressed.
 * - document: `materials/0/color` — `/` segments, RFC 6901 escaping, and
 *   collection QUALIFIERS (`components/guid:6f1f…/enabled`) that survive a
 *   reordered array.
 *
 * The document grammar is the target; this module exists so the hop happens in
 * ONE place while call sites migrate. Hand-rolled copies at the boundary are
 * what dropped edits at save time (projectFold.ts).
 */

import { decodePathSegment, encodePathSegment, joinPath, splitPath } from "@needle-tools/addressing";
import { BUILD_ENVIRONMENT } from "../../../base/common/$__metadata.js";

/** paths already reported — the inspector rebuilds rows constantly, and a
 *  per-call warning would bury the console it is meant to inform */
const warnedLegacyPaths = new Set<string>();

/** test seam only */
export function __resetLegacyPathWarnings(): void {
    warnedLegacyPaths.clear();
}

/**
 * TEMPORARY — every call is a site that still mints the dotted dialect.
 *
 * The destination is that nothing produces dotted paths at all: `buildNestedAddress`
 * (propertyProvider) mints document segments, the `renderer.info.` prefix checks and
 * `navigatePropertyPath` follow, and component rows carry qualifiers. This function
 * survives only at the AI boundary, where a model will type `material.map.repeat`
 * whatever the tool schema says. Delete it everywhere else — the warning is how the
 * remaining callers announce themselves.
 *
 * `materials[0].color` → `materials/0/color`. Null for an empty path.
 *
 * @deprecated mint document paths at the source instead — see plans/one-address-vocabulary.md
 */
export function inspectorPathToDocument(path: string): string | null {
    if (!path) return null;
    // silent in production: a host page's console belongs to the site, not to our
    // migration (dev and test builds are where anyone acts on this)
    if (BUILD_ENVIRONMENT !== "production" && !warnedLegacyPaths.has(path)) {
        warnedLegacyPaths.add(path);
        console.warn(
            `[propertyPath] converting the legacy dotted path "${path}" — this call site should mint a document path (see plans/one-address-vocabulary.md).`,
        );
    }
    return convertDottedPath(path);
}

/**
 * A path from OUTSIDE the editor — a model writing tool arguments. Accepts either
 * grammar and converts silently: this tolerance is permanent, so a deprecation
 * warning would blame a caller that is doing the right thing.
 */
export function normalizeIncomingPath(path: string): string | null {
    if (!path) return null;
    if (path.includes("/")) return path;
    return convertDottedPath(path);
}

function convertDottedPath(path: string): string | null {
    const segments: string[] = [];
    let current = "";
    for (let i = 0; i < path.length; i++) {
        const char = path[i];
        if (char === ".") {
            // a bracket index already closed its segment: "a[0].b" has nothing
            // between "]" and "." to push
            if (current) segments.push(current);
            current = "";
        }
        else if (char === "[") {
            if (current) segments.push(current);
            const end = path.indexOf("]", i);
            if (end < 0) return null;   // unbalanced — refuse rather than guess
            segments.push(path.substring(i + 1, end));
            current = "";
            i = end;
        }
        else current += char;
    }
    if (current) segments.push(current);
    if (segments.length === 0) return null;
    return joinPath(segments);
}

/** `materials/0/color` → `materials[0].color`. Null for an empty path. */
export function documentPathToInspector(path: string): string | null {
    if (!path) return null;
    const segments = splitPath(path);
    let result = "";
    for (const segment of segments) {
        // a numeric segment is an array index — except in lead position, where
        // there is nothing to index
        if (result && /^\d+$/.test(segment)) result += `[${segment}]`;
        else result += result ? `.${segment}` : segment;
    }
    return result;
}

/**
 * `"<ownerId>.material.map.repeat"` → the owner and the path after it.
 *
 * The lead is everything up to the FIRST dot and is not always an id: three
 * `object.id` for scene objects, a `uuid` for orphan-inspected materials and
 * textures, and literals like `renderer` for renderer rows.
 */
export function splitInspectorAddress(address: string): { ownerId: string; path: string } | null {
    const dot = address.indexOf(".");
    if (dot <= 0) return null;
    const ownerId = address.substring(0, dot);
    const path = address.substring(dot + 1);
    if (!ownerId || !path) return null;
    return { ownerId, path };
}

/**
 * `("12", ["material", "map"])` → `"12/material/map"` — the owner and the path in
 * one string, which is what a row's `metadata.address` is. Segments are escaped;
 * the owner is a three `object.id`, a `uuid`, or a literal like `renderer`, none
 * of which need it.
 */
export function buildAddress(ownerId: string, segments: readonly string[]): string {
    return segments.length ? `${ownerId}/${joinPath(segments)}` : ownerId;
}

/**
 * A row address → its owner and a DOCUMENT path, whichever grammar it arrived in.
 * The dotted form still reaches us from stored AI arguments and unmigrated call
 * sites; it converts (and reports itself) rather than being refused.
 */
export function splitAddress(address: string): { ownerId: string; path: string } | null {
    const slash = address.indexOf("/");
    if (slash >= 0) {
        const ownerId = address.substring(0, slash);
        const path = address.substring(slash + 1);
        if (!ownerId || !path) return null;
        return { ownerId, path };
    }
    const legacy = splitInspectorAddress(address);
    if (!legacy) return null;
    const path = inspectorPathToDocument(legacy.path);
    if (!path) return null;
    return { ownerId: legacy.ownerId, path };
}

/** Escape one literal key for use as a document path segment. */
export const encodeSegment = encodePathSegment;
/** Decode one document path segment back to the literal key. */
export const decodeSegment = decodePathSegment;
